import axios from 'axios'

// Create axios instance with default config
const api = axios.create({
  baseURL: '/api',
  timeout: 120000, // 2 minutes timeout for RAG queries
  headers: {
    'Content-Type': 'application/json',
  },
})

// Request interceptor
api.interceptors.request.use(
  (config) => {
    // Add timestamp to prevent caching
    config.params = {
      ...config.params,
      _t: Date.now(),
    }
    return config
  },
  (error) => {
    return Promise.reject(error)
  }
)

// Response interceptor
api.interceptors.response.use(
  (response) => {
    return response
  },
  (error) => {
    // Handle common errors
    if (error.code === 'ECONNABORTED') {
      error.message = 'Request timeout. Please try again.'
    } else if (!error.response) {
      error.message = 'Network error. Please check your connection.'
    }
    
    return Promise.reject(error)
  }
)

// Types
export interface QueryRequest {
  question: string
  options?: {
    maxSources?: number
    includeMetadata?: boolean
    temperature?: number
  }
}

export interface QueryResponse {
  success: boolean
  data: {
    question: string
    answer: string
    sources: Array<{
      content: string
      page: number
      score: number
      category?: string
    }>
    metadata: {
      category: string
      methods: string[]
      duration: number
      queryTime: number
      timestamp: string
      chunksProcessed: number
    }
  }
}

export interface StatsResponse {
  success: boolean
  data: {
    totalChunks: number
    categories: string[]
    averageQueryTime: number
    totalQueries: number
    systemHealth: 'healthy' | 'degraded' | 'error'
  }
}

export interface HealthResponse {
  success: boolean
  data: {
    status: string
    timestamp: string
    uptime: number
    environment: string
    version: string
    memory: {
      used: number
      total: number
      external: number
    }
    services: {
      rag_engine: string
      vector_db: string
      grok_api: string
    }
  }
}

// API functions
export const queryRAG = async (request: QueryRequest): Promise<QueryResponse> => {
  const response = await api.post('/rag/query', request)
  return response.data
}

export const getStats = async (): Promise<StatsResponse> => {
  const response = await api.get('/rag/stats')
  return response.data
}

export const getCategories = async (): Promise<{ success: boolean; data: string[] }> => {
  const response = await api.get('/rag/categories')
  return response.data
}

export const getSuggestions = async (): Promise<{ success: boolean; data: string[] }> => {
  const response = await api.get('/rag/suggestions')
  return response.data
}

export const getHealth = async (): Promise<HealthResponse> => {
  const response = await api.get('/health')
  return response.data
}

export const getReadiness = async (): Promise<{ success: boolean; message?: string }> => {
  const response = await api.get('/health/ready')
  return response.data
}

export default api